import { useState } from 'react'
import { ChevronLeft, ChevronRight, Check } from 'lucide-react'
import { useLanguage } from '@afsa/shared'
import { type WebPageSection } from '@/lib/api'
import { parseHtmlString } from '@/utils/helpers/html'
import { Heading, Text } from '@/components/ui/Typography'


interface CarouselSlide {
  id: number
  image: string
  title: string
  description?: string
}

interface MissionCarouselProps {
  className?: string
  data: WebPageSection
}

const MissionCarousel = ({ className, data }: MissionCarouselProps) => {
  const { t } = useLanguage()
  const [currentSlide, setCurrentSlide] = useState(0)

  const slides: CarouselSlide[] = [
    {
      id: 1,
      image: "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760297/0419d7b9498659c65e546b484c3a84dbb59d1976_2_grc2la.png",
      title: t('newspace_convention'),
      description: t('newspace_convention_desc')
    },
    {
      id: 2,
      image: "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760296/93411e5e11cca7d6af1a48e1d3597d83bea53c12_1_nbhkmv.png",
      title: t('international_partnerships'),
      description: t('international_partnerships_desc')
    },
    {
      id: 3,
      image: "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760297/333506ab901b83e4fee5ac8a037c9429a71fb1f8_1_fgkekh.png",
      title: t('capacity_building'),
      description: t('capacity_building_desc')
    }
  ]

  const missionPoints = Array.isArray(data.listOfPurpose) ? data.listOfPurpose : []

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
  }

  const goToSlide = (index: number) => {
    setCurrentSlide(index)
  }

  return (
    <section className={`py-16 bg-wine-100 text-white ${className}`} aria-labelledby="mission-title">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        
          <div className="order-1 lg:order-1">
            <p className="text-sm font-medium font-satoshi mb-4 opacity-90 px-4 py-2 rounded-2xl bg-wine-200 inline-block">
              {t('what_we_do')}
            </p>
            
            <Heading level={3} 
              id="mission-title"
              className="  mb-8 text-white"
            >
              {/* {t('mission_title')} */}
              {data.sectionTitle}
            </Heading>

            <div className="space-y-4">
              {missionPoints.map((point, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <Check 
                    className=" w-5 h-5 md:w-6 md:h-6 mt-0.5 flex-shrink-0 bg-wine-200 rounded-full overflow-hidden p-1" 
                    aria-hidden="true"
                  />
                  <Text className="text-white">
                    {point}
                  </Text>
                </div>
              ))}
            </div>
          </div>

          {/* Right Carousel */}
          <div className="order-2 lg:order-2  md:block">
            <div className="relative">
              {/* Carousel Container */}
              <div className="relative h-80 overflow-hidden ">
                {slides.map((slide, index) => (
                  <div
                    key={slide.id}
                    className={`absolute inset-0 transition-opacity duration-500 ${
                      index === currentSlide ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <img
                      src={slide.image}
                      alt={slide.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/20" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/50 to-transparent">
                      <Heading level={4} className="font-semibold text-white mb-2">
                        {slide.title}
                      </Heading>
                      {slide.description && (
                        <Text className=" text-white">
                          <span dangerouslySetInnerHTML={parseHtmlString(slide.description)} />
                        </Text>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors duration-200"
                aria-label="Previous slide"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors duration-200"
                aria-label="Next slide"
              >
                <ChevronRight className="h-5 w-5" />
              </button>

              {/* Dots Navigation */}
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex space-x-2">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${
                      index === currentSlide
                        ? 'bg-white w-8'
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default MissionCarousel